<div class="min-h-screen bg-white font-inter text-slate-950">

  <!-- Pricing -->
  <main class="px-4 py-12 sm:px-6 lg:px-8">
    <div class="mx-auto flex max-w-page flex-col gap-8">

      <div class="flex flex-col gap-14">
        <!-- Page Heading -->
        <div class="flex max-w-xl flex-col gap-6">
          <div class="flex flex-col gap-4">
            <p class="text-body3 text-slate-500">
              Pricing
            </p>

            <h1 class="text-[3rem] font-normal leading-[1.05] tracking-[-0.04em] text-black sm:text-[4rem]">
              Start free. Scale
              <br>
              as you grow.
            </h1>
          </div>

          <p class="text-[1.35rem] leading-8 text-slate-500">
            Choose the perfect plan to start building smarter forms
            and gaining AI-powered insights from your data.
          </p>
        </div>

        <!-- Billing Toggle -->
        <div class="flex justify-center">

          <div
            class="inline-flex items-center rounded-full border border-slate-200 bg-white p-1.5 shadow-sm">

            <!-- Monthly -->
            <button
              (click)="toggleBillingPeriod('monthly')"
              class="relative inline-flex h-12 items-center justify-center rounded-full px-6 text-body2 font-medium transition-all duration-200"
              [class.bg-slate-950]="billingPeriod() === 'monthly'"
              [class.text-white]="billingPeriod() === 'monthly'"
              [class.text-slate-600]="billingPeriod() !== 'monthly'">

              Monthly

            </button>

            <!-- Yearly -->
            <button
              (click)="toggleBillingPeriod('yearly')"
              class="relative inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-body2 font-medium transition-all duration-200"
              [class.bg-slate-950]="billingPeriod() === 'yearly'"
              [class.text-white]="billingPeriod() === 'yearly'"
              [class.text-slate-600]="billingPeriod() !== 'yearly'">

        <span>
          Yearly
        </span>

              <p
                class="rounded-full px-2.5 py-1 text-[0.72rem] font-semibold leading-none"
                [class.bg-white]="billingPeriod() === 'yearly'"
                [class.text-primary-600]="billingPeriod() === 'yearly'"
                [class.bg-primary-50]="billingPeriod() !== 'yearly'"
                [class.text-primary-700]="billingPeriod() !== 'yearly'">
                Save 20%
              </p>

            </button>

          </div>

        </div>
      </div>

      <!-- Loading State -->
      @if (plansState.loading()) {
        <div class="flex justify-center py-16">
          <div class="flex flex-col items-center gap-4 text-center">
            <div class="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
            <p class="text-body1 text-slate-600">Loading pricing plans...</p>
          </div>
        </div>
      }

      <!-- Pricing Cards -->
      @if (plansState.success()) {
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-stretch xl:grid-cols-5">

          @let items = plansState.response()?.data ?? [];
          @for (plan of items; track plan.key) {

            <article
              class="relative flex min-h-[400px] flex-col overflow-hidden rounded-[2rem] px-8 py-9"
              [class.bg-slate-100]="!plan.isRecommended"
              [class.bg-[#111113]]="plan.isRecommended"
              [class.text-white]="plan.isRecommended"
              [class.text-slate-950]="!plan.isRecommended"
              [class.border]="plan.isRecommended"
              [class.border-slate-700]="plan.isRecommended">

              @if (plan.isRecommended) {
                <div class="absolute -bottom-16 -right-10 h-44 w-44 rounded-full bg-primary-600/70 blur-2xl"></div>
                <div class="absolute -bottom-20 right-24 h-40 w-40 rounded-full bg-secondary-500/50 blur-2xl"></div>
              }

              <div class="relative flex flex-col gap-8">
                <p
                  class="text-body2 font-medium"
                  [class.text-slate-400]="plan.isRecommended"
                  [class.text-slate-500]="!plan.isRecommended">
                  {{ plan.name }}
                </p>

                <!-- Price Display -->
                @if (hasPrice(plan)) {
                  <div class="flex flex-col gap-2">
                    <div class="flex items-end gap-2">
                      <p
                        class="text-[3.5rem] font-medium leading-none tracking-[-0.05em]"
                        [class.text-white]="plan.isRecommended"
                        [class.text-black]="!plan.isRecommended">
                        {{ getCurrencySymbol(plan.price!.currency) }}{{ formatPrice(getPrice(plan)!) }}
                      </p>

                      <p
                        class="pb-2 text-body2"
                        [class.text-slate-400]="plan.isRecommended"
                        [class.text-slate-500]="!plan.isRecommended">
                        {{ getBillingText() }}
                      </p>
                    </div>

                    @if (billingPeriod() === 'yearly' && getSavingsPercentage(plan) > 0) {
                      <p
                        class="text-caption font-semibold text-success-600">
                        Save {{ getSavingsPercentage(plan) }}%
                      </p>
                    }
                  </div>
                } @else {
                  <p
                    class="text-[2.5rem] font-medium leading-none tracking-[-0.05em]"
                    [class.text-white]="plan.isRecommended"
                    [class.text-black]="!plan.isRecommended">
                    {{ customPricingLabel }}
                  </p>
                }

                <p
                  class="text-[1.1rem]"
                  [class.text-slate-400]="plan.isRecommended"
                  [class.text-slate-500]="!plan.isRecommended">
                  {{ plan.description }}
                </p>

                <!-- Limits Section -->
                <div
                  class="flex flex-col gap-3 border-t pt-6"
                  [class.border-slate-600]="plan.isRecommended"
                  [class.border-slate-200]="!plan.isRecommended">

                  <p
                    class="text-caption font-semibold uppercase tracking-wide"
                    [class.text-slate-400]="plan.isRecommended"
                    [class.text-slate-500]="!plan.isRecommended">
                    Limits included
                  </p>

                  <div class="flex flex-col gap-2 text-body3">
                    @for (row of limitRows(plan); track row.label) {
                      <div
                        class="flex items-center justify-between"
                        [class.text-slate-400]="plan.isRecommended"
                        [class.text-slate-600]="!plan.isRecommended">
                        <span>
                          {{ row.label }}
                          @if (row.metric.description) {
                            <span class="block text-[0.7rem] font-normal opacity-80">
                              {{ row.metric.description }}
                            </span>
                          }
                        </span>
                        <span
                          class="font-semibold"
                          [class.text-white]="plan.isRecommended"
                          [class.text-slate-950]="!plan.isRecommended">
                          {{ row.metric.display }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div class="relative mt-auto pt-10">
                <a
                  [routerLink]="getPlanCtaLink(plan)"
                  [queryParams]="getPlanCtaQueryParams(plan)"
                  class="inline-flex h-11 w-full items-center justify-center rounded-full px-7 text-body2 font-medium border"
                  [class.bg-white]="plan.isRecommended"
                  [class.text-slate-200]="plan.isRecommended"
                  [class.border-slate-200]="!plan.isRecommended"
                  [class.border-slate-200]="plan.isRecommended"
                  [class.bg-white]="!plan.isRecommended"
                  [class.text-black]="!plan.isRecommended">
                  {{ plan.ctaText }}
                </a>
              </div>

            </article>

          }

        </div>

        <!-- Features comparison -->
        <section class="mt-10 flex flex-col gap-8">
          <h2 class="text-xl font-medium tracking-tight text-slate-950 sm:text-2xl">
            Features comparison
          </h2>

          <div class="overflow-x-auto">
            <table class="w-full min-w-[52rem] border-collapse text-left">
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="sticky left-0 z-10 bg-white px-2 py-3 text-xs font-medium uppercase tracking-wider text-slate-400">
                    Feature
                  </th>
                  @for (plan of comparisonPlans(); track plan.key) {
                    <th
                      scope="col"
                      class="px-3 py-3 text-center text-sm font-medium text-slate-950">
                      {{ plan.name }}
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of featureRows(); track row.key) {
                  <tr class="group odd:bg-slate-50/60">
                    <th
                      scope="row"
                      class="sticky left-0 z-10 bg-white px-2 py-3.5 text-left text-sm font-normal text-slate-700 group-odd:bg-slate-50">
                      {{ row.label }}
                    </th>
                    @for (plan of comparisonPlans(); track plan.key) {
                      <td class="px-3 py-3.5 text-center text-sm text-slate-600">
                        @let cell = featureCell(plan, row.key);
                        @switch (cell.kind) {
                          @case ('check') {
                            <span class="text-base font-semibold text-emerald-500" aria-label="Included">
                              ✓
                            </span>
                          }
                          @case ('dash') {
                            <span class="text-slate-300" aria-label="Not included">—</span>
                          }
                          @case ('text') {
                            <span class="text-slate-600">{{ cell.text }}</span>
                          }
                        }
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }

      <!-- Error State -->
      @if (plansState.error()) {
        <div class="flex flex-col gap-2 rounded-lg bg-error-50 p-6 text-center">
          <p class="text-body1 font-medium text-error-700">Unable to load pricing plans</p>
          <p class="text-body2 text-error-600">Please refresh the page or contact support</p>
        </div>
      }

      <p class="text-center text-body3 text-slate-500">
        Prices may vary by platform and region. Monthly and yearly billing available.
      </p>

    </div>
  </main>

</div>
